import { apiClient } from './api';
import type {
  EvaluationRequest,
  EvaluationResponse,
  EvaluationResult,
  BulkDeleteResponse,
  DeleteAllResponse,
  PhaseScore,
  RegionScore as ApiRegionScore,
  LLMExplanation,
  RegionHull,
  LandmarkDeviation,
  GradeScaleApplied,
} from '@/types/api';

// ─── Squad Orchestration Types (PTD-M5) ──────────────────────────────────────

/**
 * Per-pair F-SYN synchronisation breakdown entry.
 * Shape mirrors the backend PerPairSync Pydantic model.
 */
export interface PerPairSync {
  person_a_index: number;
  person_b_index: number;
  sync_score: number;
}

/**
 * Squad-level orchestration result computed at the END of a positional
 * team-drill evaluation.
 *
 * Returned by GET /api/evaluations/jobs/{parent_job_id}/squad.
 * Returns 404 until the squad result exists (i.e. until the parent job
 * reaches "completed" and the positional scoring is written).
 */
export interface SquadOrchestrationResult {
  /** Overall macro squad score 0–100 */
  macro_squad_score: number;
  /** Formation/spacing fidelity score 0–100, null when insufficient data */
  ffid_score: number | null;
  /** Inter-player synchronisation score 0–100, null when phases don't align */
  fsyn_score: number | null;
  /** True when fewer players were detected than template figures */
  partial_formation: boolean;
  player_count_expected: number;
  player_count_scored: number;
  per_pair_sync: PerPairSync[];
  metadata: Record<string, unknown>;
}

/**
 * Fetch the squad orchestration result for a positional team-drill.
 * GET /api/evaluations/jobs/{parentJobId}/squad → SquadOrchestrationResult
 *
 * Returns 404 until the squad result exists (written after the parent job
 * completes). Callers should treat 404 as "not yet available / not a
 * positional team-drill" — NOT an error.
 */
export async function getSquadOrchestration(
  parentJobId: string
): Promise<SquadOrchestrationResult> {
  const { data } = await apiClient.get<SquadOrchestrationResult>(
    `/evaluations/jobs/${parentJobId}/squad`
  );
  return data;
}

// ─── Positional Team-Drill Types (PTD-5/PTD-6) ───────────────────────────────

/**
 * One detected person returned by POST /api/evaluate/formation-suggest.
 * Mirrors the backend FormationPersonSuggestion Pydantic model exactly.
 */
export interface FormationPersonSuggestion {
  person_index: number;
  /** Normalized [x1, y1, x2, y2] bounding box */
  bbox: [number, number, number, number];
  /** Base64 JPEG crop as data URL */
  crop_data_url: string;
  /** Auto-suggested position label, null when no suggestion could be made */
  suggested_label: string | null;
  /** Suggestion confidence 0–1, null when suggested_label is null */
  suggestion_confidence: number | null;
}

export interface FormationSuggestRequest {
  athlete_video_id: string;
  template_id: string;
}

export interface FormationSuggestResponse {
  persons: FormationPersonSuggestion[];
  /** All position labels defined on the positional template, ordered as created */
  template_position_labels: string[];
}

/** One confirmed person→label assignment sent in TeamDrillRequest */
export interface PositionalAssignment {
  person_index: number;
  position_label: string;
}

export interface TeamDrillRequest {
  athlete_video_id: string;
  template_id: string;
  persons_to_score?: number[] | null;
  max_persons?: number;
  position_assignments?: PositionalAssignment[];
  lead_side_override?: 'left' | 'right' | null;
  shared_with_user_id?: string | null;
  /** Coach-submits-for-athlete (Goal E). See EvaluationRequest.athlete_user_id. */
  athlete_user_id?: string | null;
}

export interface TeamDrillSubmitResponse {
  parent_job_id: string;
  child_job_ids: string[];
  persons_to_score: number[];
  status: string;
  submitted_at: string;
}

/**
 * Fetch auto-suggested formation person→label assignments for a positional template.
 * POST /api/evaluate/formation-suggest → 200
 * Returns 400 when the template is not positional.
 */
export async function getFormationSuggestions(
  req: FormationSuggestRequest
): Promise<FormationSuggestResponse> {
  const { data } = await apiClient.post<FormationSuggestResponse>(
    '/evaluate/formation-suggest',
    req
  );
  return data;
}

/**
 * Submit a team-drill evaluation job.
 * POST /api/evaluate/team-drill → 202
 * Includes position_assignments when the template is positional.
 * Returns parent_job_id for polling via GET /api/evaluations/jobs/{parent_job_id}.
 */
export async function submitTeamDrill(req: TeamDrillRequest): Promise<TeamDrillSubmitResponse> {
  const { data } = await apiClient.post<TeamDrillSubmitResponse>('/evaluate/team-drill', req);
  return data;
}

// ─── Async Job Types ──────────────────────────────────────────────────────────

/**
 * Summary of one child job within a team-drill parent job.
 * Returned as part of the parent job response from
 * GET /api/evaluations/jobs/{parent_job_id}.
 */
export interface ChildJobSummary {
  job_id: string;
  /** 0-based index of this person in the original athlete video */
  team_person_index: number;
  status: 'queued' | 'processing' | 'scored' | 'completed' | 'failed';
  progress_pct: number | null;
  /** Populated once scoring completes (status === 'scored' | 'completed') */
  result_evaluation_id: string | null;
  error_message: string | null;
}

export type EvaluationJob = {
  job_id: string;
  /**
   * PS-0a (score-fast split) lifecycle:
   *   queued → processing → scored → completed
   *   (or failed at any point)
   *
   * `scored` is NON-terminal: scoring is done, LLM coaching is still generating.
   * At `scored`, result_evaluation_id is populated and the eval is fetchable;
   * llm_explanation will be null until the job reaches `completed`.
   */
  status: 'queued' | 'processing' | 'scored' | 'completed' | 'failed';
  /**
   * Pipeline stage the worker last wrote on the job row.
   *
   * `movement_preparation` replaced `3d_lifting` on 2026-10-07: the 3D lift
   * that stage was named for was deleted by the 3D retirement, so the label it
   * drove ("Building 3D model") had become untrue. The step itself is unchanged
   * — phase loading, side/camera checks, checkpoint generation and the
   * body-orientation estimate, still at 45%.
   *
   * `'3d_lifting'` stays in the union as a DEPRECATED value because jobs queued
   * before the rename carry it on their DB row; every label map resolves it to
   * the same copy. Remove it once no pre-rename job row can be polled (eval job
   * rows are purged after SPORTFORM_EVAL_JOB_RETENTION_DAYS, default 90d).
   */
  progress_stage:
    | 'pose_extraction'
    | 'movement_preparation'
    /** @deprecated pre-2026-10-07 id for `movement_preparation`. */
    | '3d_lifting'
    | 'scoring'
    | 'coaching'
    | 'saving'
    | 'completed'
    | null;
  progress_pct: number | null;
  result_evaluation_id: string | null;
  error_message: string | null;
  error_retryable: boolean;
  submitted_at: string;
  started_at: string | null;
  completed_at: string | null;
  /** Team-drill fields — present only on team_parent and team_child jobs */
  job_type?: 'solo' | 'team_parent' | 'team_child';
  parent_job_id?: string | null;
  team_person_index?: number | null;
  /** Child job summaries — present only on team_parent jobs */
  child_jobs?: ChildJobSummary[];
};

export type SubmitResponse = {
  job_id: string;
  status: string;
  is_existing_job: boolean;
  submitted_at: string;
};

/**
 * Submit an evaluation job to the async queue.
 * Returns immediately with a job_id — poll with getJobStatus to track progress.
 * POST /api/evaluate/submit → 202
 */
export async function submitEvaluation(request: EvaluationRequest): Promise<SubmitResponse> {
  const { data } = await apiClient.post<SubmitResponse>('/evaluate/submit', request);
  return data;
}

// ─── Pre-submit visibility probe (B-2026-0089 Phase 3) ───────────────────────

/**
 * Partial-body classification from the preflight probe. Mirrors the results-time
 * `metadata.partial_body` object so the pre-submit warning and the results banner
 * speak the same contract.
 */
export interface PreflightPartialBody {
  excluded_regions: string[];
  accidental_regions: string[];
  gate: number;
}

/**
 * Stance-orientation mismatch warning (Stance Orientation Tier 3). Present only
 * when the template is asymmetric with a declared lead side, no lead_side_override
 * was sent, and the athlete's detected stance differs (or detection is low
 * confidence). Advisory only — never blocks submit.
 */
export interface PreflightOrientation {
  /**
   * "orientation_mismatch_likely"   — probe-based Stance Orientation Tier 3 warning.
   * "frontal_handedness_required"   — frontal template + no athlete side declaration;
   *                                    probe skipped, always severity "advisory".
   */
  code: string;
  message: string;
  confidence: number;
  expected_lead_side: string; // "left" | "right" | "unknown"
  detected_lead_side: string; // "left" | "right" | "unknown"
  /**
   * "info"     — confident mismatch, auto-corrected by the pipeline (reassuring).
   * "advisory" — low-confidence or ambiguous detection (athlete should double-check).
   */
  severity: 'info' | 'advisory';
}

export interface PreflightResponse {
  /** True when the visibility probe ran; false when the video was missing or the probe failed. */
  checked: boolean;
  /** Present only when one or more athlete regions are off-frame; null otherwise. */
  partial_body: PreflightPartialBody | null;
  /** Present only when a stance-orientation mismatch is likely; null otherwise. */
  orientation?: PreflightOrientation | null;
}

/**
 * Run a NON-BLOCKING pre-submit visibility probe.
 * POST /api/evaluate/preflight → 200
 *
 * Creates no job, spends no budget, moves no video. The frontend calls this
 * before submit; if `partial_body.accidental_regions` is non-empty it can warn
 * the athlete to re-record before spending the evaluation. This must NEVER block
 * a good submission — callers treat any error as "proceed to submit".
 */
export async function preflightEvaluation(
  request: EvaluationRequest
): Promise<PreflightResponse> {
  const { data } = await apiClient.post<PreflightResponse>('/evaluate/preflight', request);
  return data;
}

/**
 * Poll the status of a single evaluation job.
 * GET /api/evaluations/jobs/{job_id}
 */
export async function getJobStatus(jobId: string): Promise<EvaluationJob> {
  const { data } = await apiClient.get<EvaluationJob>(`/evaluations/jobs/${jobId}`);
  return data;
}

/**
 * Fetch all in-flight jobs for the current user.
 * Useful for mount-time job discovery (refresh recovery).
 * PS-0a: includes 'scored' so jobs that have a score but are still
 * generating coaching are returned by this discovery query.
 * GET /api/evaluations/jobs?status=queued,processing,scored
 */
export async function getPendingJobs(
  status: Array<'queued' | 'processing' | 'scored'> = ['queued', 'processing', 'scored']
): Promise<EvaluationJob[]> {
  const { data } = await apiClient.get<EvaluationJob[]>('/evaluations/jobs', {
    params: { status: status.join(',') },
  });
  return data;
}

// Static interface, not Zod — catches renames at compile time. Add runtime
// validation if stale-build edge cases become a problem.
//
// Only covers the fields actually read by extractAIFeedback() and
// transformEvaluationResult() — intentionally smaller than the full
// backend EvaluationResponse Pydantic model.
interface LlmExplanation {
  overall_summary?: string;
  improvement_tips?: string[];
  strengths?: string[];
  next_steps?: string[];
}

interface ErrorAnalysisKeyIssue {
  region_label?: string;
  description?: string;
}

interface ErrorAnalysis {
  overall_severity?: string;
  num_significant_errors?: number;
  key_issues?: ErrorAnalysisKeyIssue[];
}

interface RegionScore {
  region_id: string;
  region_label?: string;
  region_name?: string;
  weighted_score?: number;
  score?: number;
  error_frames?: number[];
  average_deviation?: number;
  max_deviation?: number;
}

// Minimal representation of the backend EvaluationResponse JSON as returned
// by GET /api/evaluations/{id} and stored in evaluation result files.
export interface BackendEvaluationPayload {
  evaluation_id: string;
  template_id?: string;
  template_video_id?: string;
  athlete_video_id?: string;
  overall_score: number;
  overall_timing_score?: number;
  phase_scores?: unknown[];
  region_scores?: RegionScore[];
  error_indicators?: unknown;
  created_at?: string;
  timestamp?: string;
  frame_scores?: unknown;
  athlete_region_hulls?: unknown;
  template_region_hulls?: unknown;
  landmark_deviations?: unknown;
  error_threshold?: unknown;
  llm_feedback?: string;
  /** `null` until the job reaches `completed` — see `EvaluationResult.llm_explanation`. */
  llm_explanation?: LlmExplanation | null;
  error_analysis?: ErrorAnalysis;
  phase_mapping?: unknown;
  metadata?: {
    total_frames?: number;
    duration_seconds?: number;
    alignment_quality?: unknown;
    phase_mapping?: unknown;
    athlete_frame_count?: number;
    '3d_lifting_enabled'?: unknown;
    '3d_model'?: unknown;
    '3d_joint_count'?: unknown;
    '3d_gravity_aligned'?: unknown;
    gemini_observations?: unknown;
    gemini_model?: unknown;
    gemini_frame_count?: unknown;
    gemini_processing_time_ms?: unknown;
    gemini_error?: unknown;
    focal_person_index?: unknown;
    person_count?: unknown;
    economy_of_motion?: unknown;
    eom_narrative?: unknown;
    holistic_trajectory_observations?: unknown;
    holistic_trajectory_regions?: unknown;
    trail_enabled_by_default?: unknown;
    /** PTD-10: position label for this child job (e.g. "PG"). Absent for solo/squad evals. */
    focal_figure_label?: string | null;
    /** BD-7 grade-feel scoring (track B). See GradeScaleApplied / EvaluationResult.metadata for field docs. */
    raw_match_score?: number | null;
    grade_scale_applied?: GradeScaleApplied | null;
  };
  spatial_goal_results?: unknown;
  overall_procrustes_score?: number;
  automatic_fail?: boolean;
  gate_failures?: unknown;
  mirrored?: boolean;
  template_lead_side?: string;
  athlete_lead_side?: string;
  athlete_frame_count?: number;
  created_by_display_name?: string;
  shared_with_user_id?: string;
  shared_with_display_name?: string;
}

/**
 * Extract AI explanation and tips from various response formats
 */
function extractAIFeedback(apiEval: BackendEvaluationPayload): { explanation?: string; tips: string[] } {
  let explanation: string | undefined;
  let tips: string[] = [];

  if (apiEval.llm_explanation) {
    explanation = apiEval.llm_explanation.overall_summary;
    // CRITICAL: copy — do NOT alias llm_explanation.improvement_tips.
    // This legacy transform previously did `tips = improvement_tips || []`
    // and then unshift/push-ed into it, MUTATING the shared llm_explanation
    // object in place. Every component reading llm_explanation afterwards
    // (EvalEditorialCoaching's "Focus on") rendered a fabricated
    // "Strengths: ..." line plus next_steps duplicated into the tips list.
    // Root cause of the owner-reported coaching echo/duplicates
    // (2026-07-28→30) — the server payload was always clean.
    tips = [...(apiEval.llm_explanation.improvement_tips || [])];

    if ((apiEval.llm_explanation.strengths?.length ?? 0) > 0) {
      tips.unshift(`Strengths: ${apiEval.llm_explanation.strengths!.join(', ')}`);
    }

    if ((apiEval.llm_explanation.next_steps?.length ?? 0) > 0) {
      tips.push(...apiEval.llm_explanation.next_steps!);
    }
  } else if (apiEval.llm_feedback) {
    explanation = apiEval.llm_feedback;
  } else if (apiEval.error_analysis) {
    const analysis = apiEval.error_analysis;
    explanation = `Overall performance: ${analysis.overall_severity}. ${analysis.num_significant_errors} significant issues found.`;

    if ((analysis.key_issues?.length ?? 0) > 0) {
      tips = analysis.key_issues!.map((issue: ErrorAnalysisKeyIssue) =>
        `${issue.region_label || 'Overall'}: ${issue.description}`
      );
    }
  }

  return { explanation, tips };
}

/**
 * Transform backend evaluation response to frontend format
 */
export function transformEvaluationResult(apiEval: BackendEvaluationPayload): EvaluationResult {
  const { explanation, tips } = extractAIFeedback(apiEval);

  // Map backend region scores to output format.
  const region_scores = (apiEval.region_scores || []).map((region: RegionScore) => ({
    region_id: region.region_id,
    region_name: region.region_label || region.region_name || '',
    score: region.weighted_score ?? region.score ?? 0,
    error_frames: region.error_frames || [],
    average_deviation: region.average_deviation ?? 0,
    max_deviation: region.max_deviation ?? 0,
  }));

  // The BackendEvaluationPayload input interface uses `unknown` / local sub-types
  // for fields that are forwarded verbatim to EvaluationResult. The cast below is
  // intentional: BackendEvaluationPayload ensures the *input* shape is known at
  // compile time, and the verbatim-forwarded fields keep their runtime contracts
  // from before this refactor. Adding a full Zod runtime validator is tracked by
  // the TODO comment on EvaluationSummaryItem above.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const payload = apiEval as any;

  return {
    evaluation_id: payload.evaluation_id,
    template_id: payload.template_id,
    template_video_id: payload.template_video_id,
    athlete_video_id: payload.athlete_video_id,
    overall_score: payload.overall_score,
    overall_timing_score: payload.overall_timing_score,
    phase_scores: payload.phase_scores,
    region_scores,
    error_indicators: payload.error_indicators,
    ai_explanation: explanation,
    improvement_tips: tips,
    created_at: payload.timestamp || payload.created_at,
    frame_scores: payload.frame_scores,
    athlete_region_hulls: payload.athlete_region_hulls,
    template_region_hulls: payload.template_region_hulls,
    landmark_deviations: payload.landmark_deviations,
    error_threshold: payload.error_threshold,
    llm_feedback: payload.llm_feedback,
    llm_explanation: payload.llm_explanation,
    error_analysis: payload.error_analysis,
    phase_mapping: payload.phase_mapping || payload.metadata?.phase_mapping || null,
    // Per-goal results. The top-level copy is what the backend emits today
    // and what every consumer reads; the metadata fallback mirrors the
    // phase_mapping line above, because the Phase 1 contract describes the
    // per-goal observability fields (status / abstention_reason /
    // degrade_reason / template_annotation_id) as living on
    // `metadata.spatial_goal_results[<key>]`. Accepting either dict means the
    // per-checkpoint UI cannot go dark on a curated-response wording
    // difference — the exact "two dicts" trap CLAUDE.md warns about. No
    // behaviour change while the top-level copy is present.
    spatial_goal_results:
      payload.spatial_goal_results || payload.metadata?.spatial_goal_results || undefined,
    overall_procrustes_score: payload.overall_procrustes_score,
    automatic_fail: payload.automatic_fail ?? false,
    gate_failures: payload.gate_failures || undefined,
    mirrored: payload.mirrored ?? undefined,
    template_lead_side: payload.template_lead_side ?? undefined,
    athlete_lead_side: payload.athlete_lead_side ?? undefined,
    created_by_display_name: payload.created_by_display_name ?? undefined,
    shared_with_user_id: payload.shared_with_user_id ?? undefined,
    shared_with_display_name: payload.shared_with_display_name ?? undefined,
    metadata: {
      total_frames: payload.athlete_frame_count || payload.metadata?.total_frames || 0,
      duration_seconds: payload.metadata?.duration_seconds || 0,
      alignment_quality: payload.metadata?.alignment_quality,
      // 3D lifting fields (passed through verbatim from backend)
      '3d_lifting_enabled': payload.metadata?.['3d_lifting_enabled'],
      '3d_model': payload.metadata?.['3d_model'],
      '3d_joint_count': payload.metadata?.['3d_joint_count'],
      '3d_gravity_aligned': payload.metadata?.['3d_gravity_aligned'],
      // Gemini Vision fields (passed through verbatim from backend)
      gemini_observations: payload.metadata?.gemini_observations,
      gemini_model: payload.metadata?.gemini_model,
      gemini_frame_count: payload.metadata?.gemini_frame_count,
      gemini_processing_time_ms: payload.metadata?.gemini_processing_time_ms,
      gemini_error: payload.metadata?.gemini_error,
      // Multi-person selection
      focal_person_index: payload.metadata?.focal_person_index,
      person_count: payload.metadata?.person_count,
      // Gates step 2/3 — rotation-boundary ceiling verdict (one verdict, many renderings)
      boundary_ceiling_applied: payload.metadata?.boundary_ceiling_applied,
      // Economy of Motion — per-phase economy scores
      economy_of_motion: payload.metadata?.economy_of_motion,
      // EOM Narrative — structured coaching narrative with chain grouping and insights
      eom_narrative: payload.metadata?.eom_narrative,
      // Holistic trajectory observations
      holistic_trajectory_observations: payload.metadata?.holistic_trajectory_observations,
      holistic_trajectory_regions: payload.metadata?.holistic_trajectory_regions,
      // Trail default state — auto-enables trajectory trail on evaluation load when true
      trail_enabled_by_default: payload.metadata?.trail_enabled_by_default,
      // PTD-10: position label for positional team-drill child evals (absent for solo/squad)
      focal_figure_label: payload.metadata?.focal_figure_label ?? null,
      // BD-7 grade-feel scoring (track B) — passed through verbatim from backend.
      // NOTE: this metadata object is reconstructed field-by-field rather than
      // spread from payload.metadata, so any new backend metadata key needs an
      // explicit line here too — the same "two-dicts" trap CLAUDE.md warns
      // about, one layer further out (this transform is a THIRD allowlist
      // beyond the backend's internal/curated split).
      raw_match_score: payload.metadata?.raw_match_score ?? null,
      grade_scale_applied: payload.metadata?.grade_scale_applied ?? null,
      // Checkpoint coverage (viewpoint observability Phase 1) — how many of the
      // coach's checkpoints this video could actually be scored against, and
      // which were withheld and why. Null for every pre-contract evaluation.
      coverage: payload.metadata?.coverage ?? null,
    },
  };
}

/**
 * Evaluate athlete video against template.
 * @deprecated POST /evaluate/with-explanation returns 410 Gone.
 * Use submitEvaluation() + useEvaluationJob() for the async flow instead.
 * This stub is kept so that existing call-sites fail loudly with a clear message
 * rather than a runtime 410 from the server.
 */
export async function evaluateVideo(_request: EvaluationRequest): Promise<EvaluationResponse> {
  throw new Error(
    'evaluateVideo is deprecated. Use submitEvaluation() + useEvaluationJob() for async evaluations.'
  );
}

/**
 * Get all evaluations
 */
export async function getEvaluations(): Promise<EvaluationResult[]> {
  const response = await apiClient.get<BackendEvaluationPayload[]>('/evaluations');
  return response.data.map(transformEvaluationResult);
}

/**
 * Get a single evaluation by ID
 */
export async function getEvaluation(evaluationId: string): Promise<EvaluationResult> {
  const response = await apiClient.get<BackendEvaluationPayload>(`/evaluations/${evaluationId}`);
  return transformEvaluationResult(response.data);
}

/**
 * Delete an evaluation
 */
export async function deleteEvaluation(evaluationId: string): Promise<void> {
  await apiClient.delete(`/evaluations/${evaluationId}`);
}

/**
 * Get evaluations for a specific athlete video
 */
export async function getEvaluationsForVideo(videoId: string): Promise<EvaluationResult[]> {
  const response = await apiClient.get<BackendEvaluationPayload[]>(
    `/evaluations?athlete_video_id=${videoId}`
  );
  return response.data.map(transformEvaluationResult);
}

/**
 * Delete multiple evaluations by ID
 */
export async function bulkDeleteEvaluations(
  evaluationIds: string[]
): Promise<BulkDeleteResponse> {
  const response = await apiClient.post<BulkDeleteResponse>(
    '/evaluations/bulk-delete',
    { evaluation_ids: evaluationIds }
  );
  return response.data;
}

/**
 * Delete all evaluations (requires confirmation)
 */
export async function deleteAllEvaluations(): Promise<DeleteAllResponse> {
  const response = await apiClient.delete<DeleteAllResponse>(
    '/evaluations/all',
    { params: { confirmation: 'DELETE_ALL_EVALUATIONS' } }
  );
  return response.data;
}

/**
 * Save the athlete video for an evaluation permanently on the server.
 * Only available to paid-tier users. After saving, the video is accessible
 * via the standard server streaming URL indefinitely.
 */
export async function saveEvaluationVideo(evaluationId: string): Promise<void> {
  await apiClient.post(`/evaluations/${evaluationId}/save-video`);
}

/**
 * Delete the athlete video for an evaluation from the server.
 * Used when a user wants to reclaim storage quota.
 */
export async function deleteEvaluationVideo(evaluationId: string): Promise<void> {
  await apiClient.delete(`/evaluations/${evaluationId}/video`);
}

// ─── User-scoped evaluation types ────────────────────────────────────────────

export interface EvaluationSummaryItem {
  evaluation_id: string;
  template_id: string;
  template_name: string | null;
  athlete_video_id: string;
  overall_score: number | null;
  form_score: number | null;
  timing_score: number | null;
  created_at: string;
  video_expires_at: string | null;
  video_deleted_at: string | null;
  created_by_user_id: string | null;
  phase_scores?: PhaseScore[];
  /** `null` until the job reaches `completed` — see `EvaluationResult.llm_explanation`. */
  llm_explanation?: LLMExplanation | null;
  region_scores?: ApiRegionScore[];
  /** Phase A: LLM coaching faithfulness score 0-1, null if not yet computed */
  coaching_faithfulness_score?: number | null;
  /** Tier A2: channel UUID — enables per-channel last-activity computation on MyChannelsCard */
  channel_id?: string | null;
  /** Phase A: body-part observation types from BiomechanicalObservation (null on legacy rows) */
  observation_types?: string[] | null;
  /** Attribution: display name of the coach who triggered this evaluation.
   *  Null for self-evals (athlete submitted their own video). */
  created_by_display_name?: string | null;
  /**
   * Display name of the evaluation's subject (the athlete/user_id). NOT YET
   * populated by the backend — `EvaluationSummaryItem` (user_routes.py) has
   * no `user_display_name` field today; this is the planned field once the
   * backend joins `User` on `user_id` (see CoachDashboard.tsx TODO(Tranche-D)).
   * Declared here (optional) so callers can prefer it over
   * `created_by_display_name` without an `any` cast; it will simply be
   * `undefined` until the backend ships it.
   */
  user_display_name?: string | null;
  /**
   * ID of the coach this evaluation was shared with at submit time.
   * Null when not shared. The coach gains read + comment access and is
   * notified via evaluation_shared_with_you on completion.
   */
  shared_with_user_id?: string | null;
  /**
   * Display name of the coach this evaluation was shared with.
   * Populated when shared_with_user_id is set.
   */
  shared_with_display_name?: string | null;
  /**
   * Number of non-deleted comments on this evaluation authored by the
   * requesting user. Used by SharedEvalsInboxCard to filter to evals the
   * coach hasn't yet commented on (comment_count_by_me === 0). Optional in
   * the type because legacy/non-listing payloads may omit it (defaults to 0
   * server-side; treat absent as 0 client-side).
   */
  comment_count_by_me?: number;
  /**
   * Goal Score follow-ups batch 1 (frontend). Mirrors
   * `docs/goal_score/F_ui_ux_designs.md` §6 and `REPORT.md` §2: `Total =
   * goal_bonus_score x form_effect`, both carried alongside Form on the
   * athlete dashboard. All three are `null` on legacy rows and whenever the
   * Goal Score switches are off (today's prod default) — `null` is never
   * `0` (LFT-063 sentinel convention); optional because older cached
   * responses omit the keys entirely.
   */
  total_score?: number | null;
  goal_bonus_score?: number | null;
  form_effect?: number | null;
  /**
   * The evaluation's publish state on the leaderboard (PR-6). `null` when
   * not applicable (no qualifying Goal). `'awaiting_athlete'` (Goal Score
   * go-live B1) means this is a coach-submitted evaluation the subject
   * athlete has not yet answered Yes/No on — see
   * `PublishConsentPromptCard` on the athlete dashboard.
   */
  publish_state?: 'private' | 'published' | 'hidden' | 'awaiting_athlete' | null;
}

export interface ProgressPoint {
  evaluation_id: string;
  created_at: string;
  overall_score: number;
  form_score: number | null;
  timing_score: number | null;
  /** Goal Score follow-ups batch 1 (frontend) — see `EvaluationSummaryItem` above for contract/null semantics. */
  total_score?: number | null;
  goal_bonus_score?: number | null;
  form_effect?: number | null;
}

export interface MyEvaluationsParams {
  template_id?: string;
  page?: number;
  page_size?: number;
  /**
   * When true, returns evaluations where the current user is the share-target
   * (shared_with_user_id == me), not the submitter.
   * Used by SharedEvalsInboxCard on the coach dashboard.
   */
  shared_with_me?: boolean;
  /**
   * Goal Score go-live B5a: filter to one publish_state value (e.g.
   * 'awaiting_athlete'). Uncapped by `page_size`'s recency window — used by
   * the athlete dashboard's publish-consent prompt so an awaiting row older
   * than the newest `page_size` evaluations is still found.
   */
  publish_state?: string;
}

/**
 * Fetch evaluations scoped to the authenticated user.
 * Returns evals where the user is the subject or creator.
 */
export async function getMyEvaluations(
  params?: MyEvaluationsParams
): Promise<EvaluationSummaryItem[]> {
  const response = await apiClient.get<EvaluationSummaryItem[]>('/users/me/evaluations', {
    params,
  });
  return response.data;
}

/**
 * Fetch per-template progress points for the authenticated user.
 */
export async function getMyProgress(templateId: string): Promise<ProgressPoint[]> {
  const response = await apiClient.get<ProgressPoint[]>(
    `/users/me/progress/${templateId}`
  );
  return response.data;
}

// ─── Coach athlete eval-history types ───────────────────────────────────────

export interface CoachAthleteEvalHistoryRow {
  user_id: string;
  display_name: string;
  email: string;
  eval_count: number;
  last_eval_at: string; // ISO
  avg_overall_score: number | null;
  avg_body_form_score: number | null;
  avg_eom_score: number | null;
  channels: { slug: string; name: string }[];
}

export interface CoachAthleteEvalHistoryParams {
  limit?: number;
  offset?: number;
}

/**
 * Fetch eval-history roll-up for all athletes across the coach's channels.
 * Returns 404 when the endpoint is not yet deployed; callers should handle
 * gracefully by catching AxiosError with status 404.
 */
export async function getCoachAthleteEvalHistory(
  params?: CoachAthleteEvalHistoryParams
): Promise<CoachAthleteEvalHistoryRow[]> {
  // Backend returns AthleteEvalHistoryListResponse: {items, total, limit, offset}.
  // The TypeScript type (and all callers — AthleteRollupTable, CoachDashboard
  // activeThisWeek, AthletesPage) treat this as a bare array. Unwrap .items
  // here so the runtime matches the declared contract.
  const response = await apiClient.get<{
    items: CoachAthleteEvalHistoryRow[];
    total: number;
    limit: number;
    offset: number;
  }>('/coach/me/athletes/eval-history', { params });
  return response.data.items ?? [];
}

// ─── Per-user eval-history types ─────────────────────────────────────────────

export interface UserEvalHistoryRow {
  evaluation_id: string;
  template_name: string;
  template_id: string;
  created_at: string;
  overall_score: number | null;
  body_form_score: number | null;
  eom_score: number | null;
  channel_slug: string | null;
  created_by_user_id: string | null;
  created_by_display_name: string | null;
  /** Phase A: display name of the athlete subject (present on all rows for the same user) */
  display_name?: string | null;
  /** Phase A: email of the athlete subject (fallback when display_name is null) */
  email?: string | null;
  /**
   * BD-7 grade-feel scoring (track B) — same shape as EvaluationResult.metadata's
   * field of the same name. Declared defensively: the `/users/{userId}/eval-history`
   * endpoint (user_routes.py) does not currently select this column (it's out of
   * scope for track B's contract, which only touches evaluation_pipeline.py's
   * metadata dicts). Absent/undefined until a follow-up backend change adds it —
   * AthleteHistoryPage tolerates that by rendering no annotation, same as any
   * other legacy/absent case.
   */
  grade_scale_applied?: GradeScaleApplied | null;
}

export interface UserEvalHistoryParams {
  limit?: number;
  offset?: number;
}

/**
 * Fetch evaluation history for a specific user.
 * Returns 403 when the caller has no channel relationship with the user.
 * Returns 404 when the endpoint is not yet deployed.
 */
export async function getUserEvalHistory(
  userId: string,
  params?: UserEvalHistoryParams
): Promise<UserEvalHistoryRow[]> {
  // Backend returns EvalHistoryListResponse: {items, total, limit, offset}.
  // Unwrap .items here so the declared array contract holds at runtime
  // (matches the sibling getCoachAthleteEvalHistory fix).
  const response = await apiClient.get<{
    items: UserEvalHistoryRow[];
    total: number;
    limit: number;
    offset: number;
  }>(`/users/${userId}/eval-history`, { params });
  return response.data.items ?? [];
}

/**
 * Fetch hull data for an evaluation separately from the main result payload.
 * The main evaluation response no longer includes hull data (stripped to reduce
 * file size by 70-75%). This endpoint serves hulls on demand so the page can
 * render scores and coaching immediately without waiting for hull data.
 */
export async function getEvaluationHulls(evaluationId: string): Promise<{
  athlete_region_hulls: RegionHull[];
  template_region_hulls: RegionHull[];
  landmark_deviations: LandmarkDeviation[] | null;
}> {
  const response = await apiClient.get<{
    athlete_region_hulls: RegionHull[];
    template_region_hulls: RegionHull[];
    landmark_deviations: LandmarkDeviation[] | null;
  }>(`/evaluations/${evaluationId}/hulls`);
  return response.data;
}
